import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EndSessionRequest, EventError,
  EventMessage,
  EventType,
} from '@azure/msal-browser';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { ConnectedUser } from '../types/connected-user.interface';
import { ApiUserRolesGateway } from '@secondaries/scenario/gateways/user-roles/api-user-roles.gateway';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public connectedUser: BehaviorSubject<ConnectedUser | null> =
    new BehaviorSubject<ConnectedUser | null>(null);
  public rolesLoaded$: ReplaySubject<void> = new ReplaySubject<void>(1);
  private readonly userRolesGateway = new ApiUserRolesGateway();
  private roles : string[] = [];
  constructor(
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly msalService: MsalService,
  ) {
    this._activeFirstAccountFromSessionStorage();
    this._loadActiveAccount();

    this.msalBroadcastService.msalSubject$.subscribe({
      next: (event: EventMessage) => {
        switch (event.eventType) {
          case EventType.LOGIN_SUCCESS:
          case EventType.ACQUIRE_TOKEN_SUCCESS: {
            const payload = event.payload as AuthenticationResult;
            this._onLogin(payload.account);
            break;
          }

          case EventType.LOGOUT_SUCCESS: {
            this._onLogout();
            break;
          }

          case EventType.LOGIN_FAILURE:
          case EventType.ACQUIRE_TOKEN_FAILURE: {
            this._onLoginFailure(event.error);
            break;
          }

          default: {
            break;
          }
        }
      },
    });
  }

  public logout(logoutRequest?: EndSessionRequest) {
    this.msalService.logout(logoutRequest);
  }

  private _onLogin(account: AccountInfo | null) {
    if (!account) {
      return;
    }

    this.msalService.instance.setActiveAccount(account);
    this._loadActiveAccount();
  }

  private _onLogout() {
    this.connectedUser.next(null);
  }

  private _onLoginFailure(error: EventError) {
    this.connectedUser.next(null);
    console.error(error);
  }

  private _accountInfoToConnectedUser(accountInfo: AccountInfo | null): null | ConnectedUser {
    if (!accountInfo) return null;

    return {
      username: accountInfo.name,
      roles: this.roles,
      preferred_username: accountInfo.idTokenClaims?.preferred_username,
    } as ConnectedUser;
  }

  private async _loadActiveAccount() {
    if (!this.msalService.instance.getActiveAccount()) {
      return;
    }

    await this._loadRolesFromBackend();

    const activeAccount = this._accountInfoToConnectedUser(
      this.msalService.instance.getActiveAccount(),
    );
    this.connectedUser.next(activeAccount);
    this.rolesLoaded$.next();
  }

  private async _loadRolesFromBackend(): Promise<void> {
    try {
      const userRoles = await this.userRolesGateway.retrieve();
      if (userRoles.roles && userRoles.roles.length > 0) {
        this.roles = userRoles.roles;
      }
    } catch (e) {
      console.warn('Failed to load roles from backend', e);
    }
  }

  private _activeFirstAccountFromSessionStorage() {
    const account = this.msalService.instance.getAllAccounts();
    if (account && account.length > 0) {
      this.msalService.instance.setActiveAccount(account[0]);
    }
  }
}
